import React, { useEffect, useMemo } from "react";
import { useLocalContext } from "../../context/LocalContext";
import "./Terms.css"; // uses the shared .t-* styles

const TAGS = ["Transparent Data Use", "No Spam", "Your Data, Your Control"];

const PrivacyPolicy = () => {
  const { webinfo = {} } = useLocalContext();
  const brand = webinfo.name || "Our Store";

  useEffect(() => {
    document.title = `Privacy Policy | ${brand}`;
  }, [brand]);

  const lastUpdated = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    });
  }, []);

  const sections = [
    { id: "overview", title: "1. Overview" },
    { id: "info-we-collect", title: "2. Information We Collect" },
    { id: "how-we-use", title: "3. How We Use Your Information" },
    { id: "cookies", title: "4. Cookies & Tracking Technologies" },
    { id: "sharing", title: "5. How We Share Information" },
    { id: "storage", title: "6. Data Storage & Retention" },
    { id: "security", title: "7. Security of Your Information" },
    { id: "your-rights", title: "8. Your Choices & Rights" },
    { id: "children", title: "9. Children’s Privacy" },
    { id: "intl", title: "10. International Users" },
    { id: "changes", title: "11. Changes to This Policy" },
    { id: "contact", title: "12. Contact Us" },
  ];

  // Best-effort current site URL for display
  let siteUrl = "";
  if (typeof window !== "undefined") {
    siteUrl = window.location.origin;
  }

  return (
    <div className="t-page">
      {/* Hero */}
      <header className="t-hero">
        <div className="t-hero__eyebrow">LEGAL</div>
        <h1 className="t-hero__title">Privacy Policy</h1>
        <p className="t-hero__subtitle">
          How we collect, use, and protect your information when you browse our
          site, place an order, or contact our support team.
        </p>
        <div className="t-hero__meta">Last updated: {lastUpdated}</div>
        <div className="t-hero__tags">
          {TAGS.map((t) => (
            <span key={t} className="t-pill">
              {t}
            </span>
          ))}
        </div>
      </header>

      {/* Grid */}
      <section className="t-grid">
        {/* Sidebar TOC */}
        <aside className="t-toc" aria-label="On this page">
          <div className="t-card t-toc__card">
            <div className="t-toc__title">ON THIS PAGE</div>
            <nav>
              <ol className="t-toc__list">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`}>
                      <span className="t-toc__num">
                        {s.title.split(".")[0]}
                      </span>{" "}
                      {s.title.replace(/^\d+\.\s*/, "")}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="t-card t-content">
          {/* 1. Overview */}
          <section id="overview" className="t-sec">
            <h2 className="t-h2">1. Overview</h2>
            <p>
              This Privacy Policy explains how <strong>{brand}</strong> (“we”,
              “us”, or “our”) collects, uses, and protects information when you
              visit our website, place an order for digital licenses, or contact
              us for support (collectively, the “Services”).
            </p>
            <p>
              Our goal is to use only the data we actually need, keep it secure,
              and give you clear choices about how it’s used. By using our
              Services, you agree to the practices described in this policy.
            </p>
          </section>

          {/* 2. Information We Collect */}
          <section id="info-we-collect" className="t-sec">
            <h2 className="t-h2">2. Information We Collect</h2>
            <p>We collect information in three main ways: directly from you, automatically, and from partners who help us operate the Service.</p>
            <ul className="t-list">
              <li>
                <strong>Contact & Account Details</strong> – your name, email
                address, phone number, and (where needed) billing information
                when you place an order, request support, or create an account.
              </li>
              <li>
                <strong>Order & License Information</strong> – products
                purchased, order IDs, license keys issued, payment status, and
                basic usage history (e.g., whether an order was refunded or
                replaced).
              </li>
              <li>
                <strong>Technical & Usage Data</strong> – IP address, browser
                type, device type, pages visited, actions taken on the site, and
                timestamps. We use this to keep the site secure and understand
                how visitors interact with it.
              </li>
              <li>
                <strong>Support Communications</strong> – messages, emails,
                chat transcripts, and any screenshots or files you choose to
                send us when asking for help.
              </li>
              <li>
                <strong>Approximate Location</strong> – inferred from IP address
                or similar signals, mainly to help with security (e.g., fraud
                checks) and to understand where our users come from.
              </li>
            </ul>
            <p>
              We do not intentionally collect sensitive categories of data such
              as health information, government ID numbers, or biometric data.
              If you believe you have shared such data by mistake, please let us
              know so we can remove it where feasible.
            </p>
          </section>

          {/* 3. How We Use Your Information */}
          <section id="how-we-use" className="t-sec">
            <h2 className="t-h2">3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="t-list">
              <li>
                Process your orders, deliver license keys, and send order
                confirmations.
              </li>
              <li>
                Provide customer support, including activation guidance and
                troubleshooting.
              </li>
              <li>
                Maintain and improve our website, products, and internal tools.
              </li>
              <li>
                Monitor for fraud, abuse, and suspicious activity to keep your
                account and our systems safer.
              </li>
              <li>
                Meet our legal, accounting, and tax obligations.
              </li>
              <li>
                With your consent or where permitted by law, send you helpful
                updates, tips, or offers related to our Services (you can opt
                out at any time).
              </li>
            </ul>
          </section>

          {/* 4. Cookies & Tracking */}
          <section id="cookies" className="t-sec">
            <h2 className="t-h2">4. Cookies &amp; Tracking Technologies</h2>
            <p>
              We use cookies and similar technologies to keep the site running
              smoothly and to understand how it is used.
            </p>
            <ul className="t-list">
              <li>
                <strong>Essential cookies</strong> – necessary for core
                features, such as remembering your cart or keeping you signed
                in.
              </li>
              <li>
                <strong>Analytics cookies</strong> – help us see which pages are
                popular, how people navigate the site, and where we can improve.
              </li>
              <li>
                <strong>Preference cookies</strong> – remember things like your
                language or cookie choices.
              </li>
            </ul>
            <p>
              You can usually control cookies through your browser settings and,
              if available, our on-site cookie banner. Blocking some types of
              cookies may impact how certain parts of the site work.
            </p>
          </section>

          {/* 5. Sharing */}
          <section id="sharing" className="t-sec">
            <h2 className="t-h2">5. How We Share Information</h2>
            <p>
              We do <strong>not</strong> sell your personal information. We only
              share it in limited situations:
            </p>
            <ul className="t-list">
              <li>
                <strong>Service Providers</strong> – such as payment processors,
                hosting providers, email sending services, and analytics tools.
                They are allowed to process your data only to provide services
                to us, and they must handle it securely.
              </li>
              <li>
                <strong>Legal or Safety Reasons</strong> – if we are required to
                do so by law, court order, or governmental authority, or when we
                believe it is necessary to protect our rights, property, or
                safety, or that of others.
              </li>
              <li>
                <strong>Business Changes</strong> – if we are involved in a
                merger, acquisition, or similar transaction, your information
                may be transferred as part of that process. If required, we’ll
                provide additional notice.
              </li>
            </ul>
            <p>
              We do not permit our service providers to use your personal
              information for their own unrelated marketing.
            </p>
          </section>

          {/* 6. Storage & Retention */}
          <section id="storage" className="t-sec">
            <h2 className="t-h2">6. Data Storage &amp; Retention</h2>
            <p>
              We keep your information only for as long as it is reasonably
              needed for the purposes described in this policy, including
              providing the Services, complying with legal obligations, and
              resolving disputes.
            </p>
            <p>
              For example, order records may be stored for several years to meet
              tax and accounting requirements, while some support conversations
              may be kept for a shorter time.
            </p>
            <p>
              When information is no longer needed, we aim to delete it or
              anonymize it so it can no longer be linked back to you.
            </p>
          </section>

          {/* 7. Security */}
          <section id="security" className="t-sec">
            <h2 className="t-h2">7. Security of Your Information</h2>
            <p>
              We use reasonable technical and organizational measures to protect
              your information, such as:
            </p>
            <ul className="t-list">
              <li>Encryption (HTTPS/TLS) for data in transit.</li>
              <li>
                Access controls and role-based permissions for internal systems.
              </li>
              <li>
                Monitoring for unusual or suspicious activity on our Services.
              </li>
            </ul>
            <p>
              No method of transmission or storage is completely secure, and we
              cannot guarantee absolute security. You are responsible for using
              strong passwords and keeping your account credentials private.
            </p>
          </section>

          {/* 8. Your Rights */}
          <section id="your-rights" className="t-sec">
            <h2 className="t-h2">8. Your Choices &amp; Rights</h2>
            <p>
              Depending on where you live and applicable law, you may have some
              or all of the following rights:
            </p>
            <ul className="t-list">
              <li>
                <strong>Access</strong> – request a copy of the personal data we
                hold about you.
              </li>
              <li>
                <strong>Correction</strong> – ask us to update or correct
                inaccurate information.
              </li>
              <li>
                <strong>Deletion</strong> – request deletion of some or all of
                your personal data, subject to legal and contractual limits.
              </li>
              <li>
                <strong>Restriction</strong> – request that we limit how we use
                your data in specific situations.
              </li>
              <li>
                <strong>Objection</strong> – object to certain processing,
                including direct marketing.
              </li>
              <li>
                <strong>Portability</strong> – request that we provide certain
                information in a structured, commonly used format.
              </li>
              <li>
                <strong>Marketing Preferences</strong> – opt out of marketing
                emails at any time using the unsubscribe link in those emails or
                by contacting us.
              </li>
            </ul>
            <p>
              To exercise these rights (where available), contact us at{" "}
              <a href={`mailto:${webinfo.email || ""}`}>
                {webinfo.email || "support@example.com"}
              </a>
              . We may need to verify your identity before fulfilling your
              request.
            </p>
          </section>

          {/* 9. Children */}
          <section id="children" className="t-sec">
            <h2 className="t-h2">9. Children’s Privacy</h2>
            <p>
              Our Services are intended for adults and are not directed to
              children under 13. We do not knowingly collect personal
              information from children under 13. If you believe a child has
              provided us with personal information, please contact us so we can
              take appropriate steps to remove it.
            </p>
          </section>

          {/* 10. International */}
          <section id="intl" className="t-sec">
            <h2 className="t-h2">10. International Users</h2>
            <p>
              We may store and process information in countries other than your
              own. Those countries may have different data protection laws, but
              we will take steps to protect your information in line with this
              policy and applicable legal requirements.
            </p>
          </section>

          {/* 11. Changes */}
          <section id="changes" className="t-sec">
            <h2 className="t-h2">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do,
              we will update the “Last updated” date at the top of this page. If
              we make significant changes, we may provide additional notice
              (such as a banner on the site or an email, where appropriate).
            </p>
          </section>

          {/* 12. Contact */}
          <section id="contact" className="t-sec">
            <h2 className="t-h2">12. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or how we handle
              your information, you can contact us here:
            </p>
            <address className="t-address">
              <strong>{brand}</strong>
              <br />
              {webinfo.address && (
                <>
                  {webinfo.address}
                  <br />
                </>
              )}
              Email:{" "}
              <a href={`mailto:${webinfo.email || ""}`}>
                {webinfo.email || "support@example.com"}
              </a>
              <br />
              {webinfo.phone && (
                <>
                  Phone: <a href={`tel:${webinfo.phone}`}>{webinfo.phone}</a>
                  <br />
                </>
              )}
              {siteUrl && (
                <>
                  Website:{" "}
                  <a href={siteUrl} target="_blank" rel="noopener noreferrer">
                    {siteUrl}
                  </a>
                </>
              )}
            </address>
          </section>
        </article>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
